<template>
  <div class="zipcode">
    <div class="zipcode-box formdata address">
      <div class="header-container">
        <h3 id="address_question">Wo befindet sich das Haus?</h3>
        <span class="centered">Wir prüfen, ob wir ein Installations-Team vor Ort haben.</span>
      </div>

      <form class="input-group centered" @submit.prevent="submitAddress" novalidate>
        <div class="row">
          <div class="field field--small">
            <input id="zipcode_input" name="zipcode" class="input square-input" type="text" inputmode="numeric" minlength="5" maxlength="5" pattern="[0-9]{5}" v-model="zipCode" @input="onZipInput" :aria-describedby="zipError ? 'zipcode_error' : null" :aria-invalid="!!zipError" aria-labelledby="zipcode_label" />
            <label id="zipcode_label" for="zipcode_input">Postleitzahl</label>
            <p id="zipcode_error" v-if="zipError" class="error" aria-live="polite">{{ zipError }}</p>
          </div>

          <div class="field">
            <input id="city_input" name="city" class="input square-input" type="text" v-model.trim="city" :aria-describedby="cityError ? 'city_error' : null" :aria-invalid="!!cityError" aria-labelledby="city_label" />
            <label id="city_label" for="city_input">Ort</label>
            <p id="city_error" v-if="cityError" class="error" aria-live="polite">{{ cityError }}</p>
          </div>
        </div>

        <div class="row">
          <div class="field">
            <input id="street_input" name="street" class="input square-input" type="text" v-model.trim="street" :aria-describedby="streetError ? 'street_error' : null" :aria-invalid="!!streetError" aria-labelledby="street_label" />
            <label id="street_label" for="street_input">Straße</label>
            <p id="street_error" v-if="streetError" class="error" aria-live="polite">{{ streetError }}</p>
          </div>

          <div class="field field--small">
            <input id="housenumber_input" name="houseNumber" class="input square-input" type="text" v-model.trim="houseNumber" :aria-describedby="houseNumberError ? 'housenumber_error' : null" :aria-invalid="!!houseNumberError" aria-labelledby="housenumber_label" />
            <label id="housenumber_label" for="housenumber_input">Hausnummer</label>
            <p id="housenumber_error" v-if="houseNumberError" class="error" aria-live="polite">{{ houseNumberError }}</p>
          </div>
        </div>
      </form>
    </div>

    <button
        class="button button_yellow right"
        type="button"
        @click="submitAddress"
        v-if="!isAnyFieldEmpty"
    >
      Weiter
    </button>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useWorkflowStore } from '../../stores/workflow.js'

const workflowStore = useWorkflowStore()

// lokale Refs für die Inputs
const zipCode = ref('')
const city = ref('')
const street = ref('')
const houseNumber = ref('')

// einmalige Hydration-Flag
const hydratedFromStore = ref(false)

function hydrateFromStoreOnce () {
  if (hydratedFromStore.value) return
  const b = workflowStore.workflowContext?.formData?.building
  if (!b) return

  // nur setzen, wenn im Input noch nichts steht (keine User-Eingaben überschreiben)
  if (!zipCode.value && b.zip)         zipCode.value = String(b.zip)
  if (!city.value && b.city)           city.value = String(b.city)
  if (!street.value && b.street)       street.value = String(b.street)
  if (!houseNumber.value && b.houseNumber) houseNumber.value = String(b.houseNumber)

  hydratedFromStore.value = true
}

// beim Mount versuchen zu hydratisieren …
onMounted(hydrateFromStoreOnce)
// … und falls der Store später kommt, nochmal (aber nur einmal)
watch(
    () => workflowStore.workflowContext?.formData?.building,
    () => hydrateFromStoreOnce(),
    { deep: true, immediate: true }
)


// Validierungen
const zipFormatValid = computed(() => /^\d{5}$/.test(zipCode.value))
const cityFormatValid = computed(() => city.value.trim().length > 1)
const streetFormatValid = computed(() => street.value.trim().length > 1)
const houseNumberFormatValid = computed(() => /^[0-9]+[a-zA-ZäöüÄÖÜ-]*$/.test(houseNumber.value.trim()))

// 1. NEU: Prüfen, ob *alle* Felder ausgefüllt sind (für Button-Anzeige)
const isAnyFieldEmpty = computed(() =>
    !zipCode.value.trim() || !city.value.trim() || !street.value.trim() || !houseNumber.value.trim()
)

// Prüft nur, ob das Format korrekt ist
const isFormatValid = computed(() =>
    zipFormatValid.value && cityFormatValid.value && streetFormatValid.value && houseNumberFormatValid.value
)

// Nur gültig, wenn alle ausgefüllt *und* im korrekten Format (für Submit-Logik)
const isFormValid = computed(() => !isAnyFieldEmpty.value && isFormatValid.value)


// Fehlertexte
// Steuert, ob die Fehlermeldungen (Punkt 2) angezeigt werden sollen
const showErrors = ref(false)

// 2. Fehlertexte nur anzeigen, wenn das Format falsch *und* showErrors = true ist
const zipError = computed(() => (zipFormatValid.value ? '' : (showErrors.value ? 'Bitte eine 5-stellige Postleitzahl eingeben.' : '')))
const cityError = computed(() => (cityFormatValid.value ? '' : (showErrors.value ? 'Bitte einen Ort angeben.' : '')))
const streetError = computed(() => (streetFormatValid.value ? '' : (showErrors.value ? 'Bitte eine Straße angeben.' : '')))
const houseNumberError = computed(() => (houseNumberFormatValid.value ? '' : (showErrors.value ? 'Bitte eine gültige Hausnummer angeben.' : '')))

// Nur Ziffern für PLZ erlauben
function onZipInput (e) {
  // Erlaubt weiterhin nur Ziffern und begrenzt auf 5 Stellen
  zipCode.value = e.target.value.replace(/\D/g, '').slice(0, 5)
  // showErrors wird hier NICHT zurückgesetzt, damit Fehler sichtbar bleiben,
  // nachdem der Submit-Button einmal gedrückt wurde.
}

function submitAddress () {
  // Beim Klick immer showErrors auf true setzen, um alle Fehler anzuzeigen
  showErrors.value = true

  // 3. Nur senden, wenn die Form vollständig ausgefüllt *und* valide ist.
  if (!isFormValid.value) {
    // Wenn die Form nicht valide ist, beenden wir hier und die Fehler werden angezeigt
    return
  }

  // Wenn valide (isFormValid = true), senden wir das Event.
  workflowStore.sendWorkflowEvent({
    type: 'NEXT',
    data: {
      zip: zipCode.value,
      city: city.value.trim(),
      street: street.value.trim(),
      houseNumber: houseNumber.value.trim()
    }
  })
}
</script>
<style scoped>

 * {
  box-sizing: border-box;
}

#app-ves-clickstream .zipcode-box.formdata.address {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: transparent;
  padding: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
}

#app-ves-clickstream #address_question {
  margin-bottom: 5px;
  text-align: center;
  width: 100%;
}

#app-ves-clickstream .centered {
  display: block;
  width: 100%;
  text-align: center;
}

#app-ves-clickstream .input-group {
  background-color: var(--input-background-color);
  padding: 30px;

  /*margin-top: 45px;
  /*display: grid;*/
  gap: 20px;
  max-width: 880px;
  width: 100%;
}


@media (min-width: 900px) {
  #app-ves-clickstream .input-group {
    max-width: 600px;
  }
}

@media (max-width: 767px) {
  #app-ves-clickstream .field {
    margin-bottom: 1.5em;
  }
  #app-ves-clickstream .input.square-input {
    font-size: 16px !important;
  }
}

@media (min-width: 768px) {
  #app-ves-clickstream .row {
    display: flex;
    gap: 1.5em;
    margin-bottom: 1.5em;
  }

  #app-ves-clickstream .field {
    flex: 2;
  }

  #app-ves-clickstream .field.field--small {
    flex: 1;
  }
}

#app-ves-clickstream .field {
  position: relative;
}

#app-ves-clickstream .input.square-input {
  width: 100%;
  padding: 14px 12px 12px;
  border: 1px solid var(--secondary-text-color);
  border-radius: 0;
  outline: none;
  background: var(--input-background-color);
  font: inherit;
}

#app-ves-clickstream .input.square-input:focus {
  border-color: var(--focus-border-color);
  box-shadow: 0 0 0 2px rgba(26,115,232,.12);
  outline:2px solid var(--outline-color);
  outline-offset:.2rem;
}

#app-ves-clickstream .field > label {
  position: absolute;
  top: -7px;
  left: 12px;
  padding: 0 6px;
  background: var(--input-background-color);
  font-size: 14px;
  line-height: 1;
  color: var(--primary-text-color);
  pointer-events: none;
}

/* Fehlermeldungen */
#app-ves-clickstream .error {
  color: var(--error-text-color);
  margin-top: 2px;
  font-size: 14px;
}


</style>